import { TurnsRemaining } from '../../coins/TurnsRemaining';
import { type ReactNode, type Ref } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { assetUrl } from '../../api/assets';
import { getCoinBalance } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { useT } from '../../i18n';
import { fmtNumber } from '../../i18n/format';
import { usePlatform } from '../../platform/platform';
import { Bdi } from '../ui/Bdi';
import { Icon } from '../ui/Icon';
import { useNavShape } from './AppNav';
import styles from './AppHeader.module.css';

export interface AppHeaderConfig {
  title?: string;
  /**
   * Unterdrueckt den Titel. In der Shell-/Seiten-Kopfzeile ohne Wirkung (dort
   * steht immer das Lorath-Logo), in der PANE-TITELZEILE dagegen ein echter
   * Vertrag: Seiten, die ihren Titel selbst in der Flaeche fuehren
   * (world-elements, world-tree, table-round-session), bestellen ihn damit ab.
   */
  hideTitle?: boolean;
  backTo?: string | (() => void);
  transparent?: boolean;
  actions?: ReactNode;
}

export interface AppHeaderProps extends AppHeaderConfig {
  coinBalance?: number | null;
  /** Nur der koordinierte Shell-Owner setzt die Platzierung explizit. */
  placement?: 'page' | 'shell' | 'pane';
  /** L1: Zugriff auf das <header>-Element fuer den Rahmen (AppShell misst die
   *  Zeilenhoehe in `--lorath-shell-header-block`). Bewusst ein Ref statt einer
   *  Suche per querySelector: so erfaehrt der Rahmen den Wechsel der Instanz,
   *  ohne bei JEDEM Render den Teilbaum abzusuchen. */
  elementRef?: Ref<HTMLElement>;
  /** L1: zusaetzliche Aktionen, die der Layout-Rahmen beisteuert (z.B. der
   *  Maximieren-Umschalter der Detailspalte) — stehen VOR den Seiten-Aktionen. */
  extraActions?: ReactNode;
}

function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

function readBalance(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function readRemaining(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, value);
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
  }
  if (value && typeof value === 'object' && 'remaining' in value) {
    return readRemaining((value as { remaining?: unknown }).remaining);
  }
  return null;
}

// Exportiert, damit Aufrufer (z.B. Composer) die Gratis-Nachrichten aus dem
// Coin-Balance-Query auf dieselbe Weise lesen koennen wie der Header-Badge.
export { readRemaining };

function stripAppTitlePrefix(value: string): string {
  const stripped = value.replace(/^Lorath\s*(?:[-\u2013\u2014]|[?]{2,})\s*/i, '').trim();
  return stripped || value;
}

export function AppHeader({
  title,
  hideTitle = false,
  backTo,
  transparent = false,
  actions,
  coinBalance,
  placement = 'page',
  extraActions,
  elementRef,
}: AppHeaderProps) {
  const { lang, t } = useT();
  const platform = usePlatform();
  const navigate = useNavigate();
  const location = useLocation();
  const balanceQuery = useQuery({
    queryKey: queryKeys.coins.balance,
    queryFn: getCoinBalance,
    // Die Pane-Titelzeile zeigt keine Coin-Pille — dann auch keine Abfrage.
    enabled: coinBalance === undefined && placement !== 'pane',
  });
  // E2/Q9: die alte Desktop-Top-Nav ist weg — ab 600 px navigiert die Rail
  // (AppNav). Der Header traegt nur noch Zurueck/Titel/Coin/Einstellungen + den
  // Rail-Umschalter (⌘0). In der Bar-Gestalt (< 600 px) gibt es keine Rail →
  // kein Umschalter.
  const { shape: navShape, toggle: toggleRail } = useNavShape();
  // L1: EINE Rolle je Kopfzeile. `placement='pane'` ist die TITELZEILE einer
  // Split-Spalte — sie traegt nur Zurueck, Titel und Aktionen. Das globale
  // Chrome (Rail-Umschalter, Wortmarke, Coin-Pille, Zahnrad) gehoert
  // AUSSCHLIESSLICH der Shell-Kopfzeile. Vorher rendert JEDE Instanz das volle
  // Chrome — daher am Laptop zwei Hamburger, zwei Wortmarken, zwei Coin-Pillen
  // nebeneinander (/settings, /chats/:id).
  const isPaneRow = placement === 'pane';
  const showRailToggle = navShape !== 'bar' && !isPaneRow;
  // Zurueck-Pfeil: nativ das plattformtypische Chevron, im Web (Desktop UND
  // mobiler Browser) einheitlich das Web-Icon. Am `os` festgemacht wuerde das
  // Icon im mobilen Web mitwandern, obwohl es dieselbe Web-Oberflaeche ist.
  const backIconName = platform.isNative ? 'chevron-left' : 'back';
  const resolvedCoinBalance = readBalance(coinBalance) ?? readBalance(balanceQuery.data?.balance);
  const freeMessages = readRemaining(balanceQuery.data?.free_messages)
    ?? readRemaining(balanceQuery.data?.free_normal_messages);
  // Negativer Saldo (z.B. nach `account_negative` vom Server) → sichtbar rot
  // einfärben, damit der User sieht, dass er auf der gesperrten Seite landet.
  // Wir zeigen weiterhin den exakten Wert (inkl. Vorzeichen), nicht `0`.
  const isNegativeBalance = resolvedCoinBalance !== null && resolvedCoinBalance < 0;
  const coinLabel = resolvedCoinBalance === null
    ? null
    : fmtNumber(resolvedCoinBalance, lang);
  const showCoinSkeleton = coinBalance === undefined && balanceQuery.isPending && coinLabel === null;
  const accessibleHeaderLabel = title?.trim()
    ? stripAppTitlePrefix(title)
    : 'Lorath';

  const handleBack = () => {
    if (typeof backTo === 'function') {
      backTo();
      return;
    }
    if (backTo) navigate(backTo);
  };

  return (
    <header
      ref={elementRef}
      className={cx(
        styles.header,
        transparent && styles.transparent,
        placement === 'pane' && styles.pane,
      )}
      data-shell-header-placement={placement}
      aria-label={accessibleHeaderLabel}
    >
      <div className={styles.left}>
        {showRailToggle ? (
          <button
            type="button"
            className={styles.iconButton}
            aria-label={t('nav.rail_toggle')}
            title={t('nav.rail_toggle')}
            onClick={toggleRail}
          >
            <Icon name="menu" size={22} />
          </button>
        ) : null}
        {backTo ? (
          <button
            type="button"
            className={styles.backButton}
            aria-label={t('common.back')}
            title={t('common.back')}
            onClick={handleBack}
          >
            <Icon name={backIconName} size={22} className={styles.backIcon} directional />
          </button>
        ) : null}

        {isPaneRow ? (
          !hideTitle && title?.trim()
            ? <h2 className={styles.paneTitle}>{stripAppTitlePrefix(title)}</h2>
            : null
        ) : (
          <Link to="/dashboard" className={styles.logoLink} aria-label={t('dashboard.title')}>
            <img
              src={assetUrl('/img/logo-wide.webp')}
              alt="Lorath"
              className={styles.logoImage}
              onError={(event) => {
                event.currentTarget.hidden = true;
              }}
            />
          </Link>
        )}
      </div>

      <div className={styles.right}>
        {extraActions ? <div className={styles.actions}>{extraActions}</div> : null}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
        {isPaneRow ? null : (
          <>
            <Link
              to="/coins"
              className={cx(styles.coinBadge, isNegativeBalance && styles.coinBadgeNegative)}
              aria-label={isNegativeBalance ? t('coins_global.balance_negative_aria') : t('nav.coins_buy')}
              title={isNegativeBalance ? t('coins_global.balance_negative_aria') : t('nav.coins_buy')}
              data-negative={isNegativeBalance ? 'true' : undefined}
            >
              <Icon name={isNegativeBalance ? 'warning' : 'coin'} size={22} />
              {showCoinSkeleton ? (
                <span className={styles.coinSkeleton} aria-hidden="true" />
              ) : coinLabel ? (
                <span className={styles.coinBalance}>{t('coins.balance_label', { coins: coinLabel })}
                  <small className={styles.turns}><TurnsRemaining compact data={balanceQuery.data ? { ...balanceQuery.data, balance: resolvedCoinBalance ?? 0 } : undefined}
                    surface={(/^\/(character-chat|chats|discover)(?:\/|$)/.test(location.pathname)) ? 'chat' : 'adventure'} /></small>
                </span>
              ) : null}
            </Link>
            {freeMessages !== null && freeMessages > 0 ? (
              <Link
                to="/coins"
                className={styles.freeBadge}
                aria-label={t('coins_global.free_messages_badge_title')}
                title={t('coins_global.free_messages_badge_title')}
              >
                <Bdi>+{fmtNumber(freeMessages, lang)}</Bdi>
              </Link>
            ) : null}
            <Link to="/settings" className={styles.iconButton} aria-label={t('nav.settings')} title={t('nav.settings')}>
              <Icon name="settings" size={22} />
            </Link>
          </>
        )}
      </div>
    </header>
  );
}
